import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, Paper, Stack, IconButton, CircularProgress } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { CVHttpService } from '../../services';
import ExperienceFormDialog from '../dialogs/ExperienceFormDialog';

function ExperiencesStep({ cvId }) {
    const [experiences, setExperiences] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingExperience, setEditingExperience] = useState(null);

    useEffect(() => {
        if (cvId) {
            loadExperiences();
        }
    }, [cvId]);

    const loadExperiences = async () => {
        if (!cvId) return;
        setLoading(true);
        try {
            const service = new (await import('core/services/http')).HttpService();
            const data = await service.get(`/job/cv/jobexperience/?cv=${cvId}`, null);
            // Handle both array response and paginated response
            const experiencesList = Array.isArray(data) ? data : (data?.results || []);
            setExperiences(experiencesList);
        } catch (err) {
            console.error('Failed to load experiences', err);
            setExperiences([]);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this experience?')) return;
        try {
            await CVHttpService.deleteJobExperience(id);
            loadExperiences();
        } catch (err) {
            alert('Failed to delete experience');
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6" fontWeight={600}>
                    Work Experience
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => {
                        setEditingExperience(null);
                        setOpenDialog(true);
                    }}
                >
                    Add Experience
                </Button>
            </Stack>

            {!Array.isArray(experiences) || experiences.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
                    <Typography color="text.secondary">
                        No work experience added yet. Click "Add Experience" to get started.
                    </Typography>
                </Paper>
            ) : (
                <Stack spacing={2}>
                    {Array.isArray(experiences) && experiences.map((exp) => (
                        <Paper key={exp.id} sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="start">
                                <Box>
                                    <Typography variant="h6" fontWeight={600}>
                                        {exp.title}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {exp.company_title || 'Company name'}
                                    </Typography>
                                    {exp.description && (
                                        <Typography variant="body2" sx={{ mt: 1 }}>
                                            {exp.description.substring(0, 100)}
                                            {exp.description.length > 100 ? '...' : ''}
                                        </Typography>
                                    )}
                                </Box>
                                <Stack direction="row" spacing={1}>
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            setEditingExperience(exp);
                                            setOpenDialog(true);
                                        }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleDelete(exp.id)}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Stack>
                            </Stack>
                        </Paper>
                    ))}
                </Stack>
            )}

            <ExperienceFormDialog
                open={openDialog}
                onClose={() => {
                    setOpenDialog(false);
                    setEditingExperience(null);
                }}
                onSave={() => {
                    loadExperiences();
                    setOpenDialog(false);
                    setEditingExperience(null);
                }}
                cvId={cvId}
                experience={editingExperience}
            />
        </Box>
    );
}

export default ExperiencesStep;

